import { useEffect, useState } from 'react';
import {
  Bell,
  KeyRound,
  Loader2,
  Mail,
  Plus,
  Printer,
  ReceiptText,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useCustomerSettings,
  useInviteTeammate,
  useRemoveTeammate,
  useUpdateCustomerSettings,
  type BillingRole,
  type SelfServeRole,
  type TeamMember,
  type UpdateSettingsInput,
} from '@/hooks/useCustomerSettings';

/**
 * Shared Settings panels. Extracted from the former single-page Settings.tsx
 * so the team + billing-notification sections can each live in their own tab
 * of the Settings hub without duplicating logic.
 *
 * Management UI is gated on `is_admin` (resolved server-side and echoed in the
 * settings payload). Non-admin members see everything read-only.
 */

type SettingsData = NonNullable<ReturnType<typeof useCustomerSettings>['data']>;

/* ============================== Your team ============================== */

const TEAM_ROLE_OPTIONS: ReadonlyArray<{ value: SelfServeRole; label: string }> = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin (can manage team)' },
];

function roleLabel(role: string): string {
  if (role === 'admin' || role === 'owner') return 'Admin';
  if (role === 'member') return 'Member';
  if (role === 'location_manager') return 'Location manager';
  if (role === 'ap') return 'Accounts payable';
  return role;
}

export function TeamSettings({ data }: { data: SettingsData }) {
  const isAdmin = data.is_admin;
  const team = data.team;

  return (
    <Section
      icon={<Users size={18} />}
      title="Your team"
      subtitle="People who can sign in to this account."
    >
      {isAdmin && <InviteForm />}
      <TeamList team={team} isAdmin={isAdmin} />
    </Section>
  );
}

function TeamList({ team, isAdmin }: { team: TeamMember[]; isAdmin: boolean }) {
  const remove = useRemoveTeammate();
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  return (
    <div className={isAdmin ? 'mt-5' : ''}>
      <div className="mb-2 text-xs font-semibold tracking-tight text-slate-900">
        Team members ({team.length})
      </div>
      {feedback && (
        <div
          className={cn(
            'mb-2 rounded-lg border px-3 py-2 text-xs tracking-tight',
            feedback.kind === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          {feedback.msg}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-brix-border bg-brix-surface">
        {team.length === 0 ? (
          <div className="px-4 py-6 text-sm tracking-tight text-slate-900">
            No team members yet.
          </div>
        ) : (
          team.map((u, i) => {
            const memberIsAdmin = u.role === 'admin' || u.role === 'owner';
            return (
              <div
                key={u.id}
                className={cn(
                  'flex flex-wrap items-center justify-between gap-3 px-4 py-3',
                  i !== team.length - 1 && 'border-b border-brix-border',
                )}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold tracking-tight text-slate-900">
                    {u.full_name ?? u.email ?? u.id}
                    {memberIsAdmin && (
                      <span className="border-brix-primary/20 ml-2 inline-flex items-center gap-1 rounded-md border bg-brix-primary-tint px-2 py-0.5 text-[10px] font-semibold text-brix-primary-deep">
                        <ShieldCheck size={10} /> Admin
                      </span>
                    )}
                  </div>
                  <div className="text-xs tracking-tight text-slate-900">
                    {u.email}
                    <span className="text-slate-900/60"> · {roleLabel(u.role)}</span>
                    {u.last_sign_in_at && (
                      <span className="text-slate-900/70">
                        {' '}
                        · last seen {new Date(u.last_sign_in_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirm({ id: u.id, name: u.full_name ?? u.email ?? u.id })}
                    className="text-red-600 hover:text-red-700"
                    title="Remove from this account"
                  >
                    <Trash2 size={12} /> Remove
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title="Remove team member?"
          body={
            <>
              <p>
                Remove <strong className="font-semibold">{confirm.name}</strong> from this account?
              </p>
              <p className="mt-2 text-xs italic text-slate-900/70">
                Their sign-in stays valid but they'll lose access to your data until you re-invite
                them. You can't remove the last admin.
              </p>
            </>
          }
          confirmLabel="Remove"
          danger
          loading={remove.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            setFeedback(null);
            try {
              await remove.mutateAsync({ user_id: confirm.id });
              setFeedback({ kind: 'ok', msg: `Removed ${confirm.name}.` });
              setConfirm(null);
            } catch (err) {
              setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : String(err) });
              setConfirm(null);
            }
          }}
        />
      )}
    </div>
  );
}

function InviteForm() {
  const invite = useInviteTeammate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<SelfServeRole>('member');
  const [sendWelcome, setSendWelcome] = useState(true);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setFeedback(null);
        try {
          const r = await invite.mutateAsync({
            email: email.trim(),
            full_name: fullName.trim(),
            phone: phone.trim() || undefined,
            role,
            send_welcome: sendWelcome,
          });
          const verb =
            r.membership_action === 'already_active'
              ? 'is already on this account'
              : r.membership_action === 'reactivated'
                ? 'was re-added'
                : r.membership_action === 'updated'
                  ? 'was updated'
                  : 'was invited';
          setFeedback({
            kind: 'ok',
            msg: r.email_sent
              ? `${r.email} ${verb}. A welcome email is on the way.`
              : `${r.email} ${verb}.`,
          });
          setEmail('');
          setFullName('');
          setPhone('');
          setRole('member');
        } catch (err) {
          setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : String(err) });
        }
      }}
      className="bg-brix-surface-alt/40 rounded-xl border border-brix-border px-4 py-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <UserPlus size={16} className="text-brix-primary-deep" />
        <h3 className="font-display text-base font-bold tracking-tight text-slate-900">
          Invite a team member
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <FieldLabel label="Full name">
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Doe"
            required
          />
        </FieldLabel>
        <FieldLabel label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@theirdomain.com"
            required
          />
        </FieldLabel>
        <FieldLabel label="Phone (helps us recognize them when they call)">
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(510) 555-1234"
          />
        </FieldLabel>
        <FieldLabel label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as SelfServeRole)}
            className="h-9 w-full rounded-md border border-brix-border bg-brix-surface px-2 text-sm tracking-tight text-slate-900"
          >
            {TEAM_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FieldLabel>
      </div>

      <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs tracking-tight text-slate-900">
        <input
          type="checkbox"
          checked={sendWelcome}
          onChange={(e) => setSendWelcome(e.target.checked)}
        />
        <Mail size={12} /> Send a welcome email with sign-in instructions
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={invite.isPending}>
          {invite.isPending ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          {invite.isPending ? 'Inviting…' : 'Send invite'}
        </Button>
        {feedback && (
          <span
            className={cn(
              'text-xs tracking-tight',
              feedback.kind === 'ok' ? 'text-emerald-700' : 'text-red-600',
            )}
          >
            {feedback.msg}
          </span>
        )}
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-[11px] italic tracking-tight text-slate-900/70">
        <KeyRound size={11} className="mt-0.5 shrink-0" />
        Admins can manage the team and billing notifications. Members can place orders and view the
        account.
      </p>
    </form>
  );
}

/* ========================= Billing notifications ========================= */

type RoutingKey =
  | 'invoice_recipients'
  | 'statement_recipients'
  | 'reminder_recipients'
  | 'order_update_recipients';

const ROUTING_FIELDS: ReadonlyArray<{
  key: RoutingKey;
  label: string;
  chip: string;
}> = [
  { key: 'invoice_recipients', label: 'New invoice emails', chip: 'Invoices' },
  { key: 'statement_recipients', label: 'Monthly statement emails', chip: 'Statements' },
  { key: 'reminder_recipients', label: 'Overdue reminder emails', chip: 'Reminders' },
  { key: 'order_update_recipients', label: 'Order status emails', chip: 'Order updates' },
];

// UI vocabulary (2026-07-08): storage keeps the legacy column/role names;
// only the labels changed. 'optional' is the fourth slot.
const EMAIL_FIELDS: ReadonlyArray<{
  key: 'billing_email' | 'accounting_email' | 'remittance_email' | 'optional_email';
  label: string;
  role: BillingRole;
}> = [
  { key: 'billing_email', label: 'Primary Email', role: 'billing' },
  { key: 'remittance_email', label: 'Secondary Email', role: 'remittance' },
  { key: 'optional_email', label: 'Optional Email', role: 'optional' },
  { key: 'accounting_email', label: 'Accounting Email', role: 'accounting' },
];

export function BillingNotifications({ data }: { data: SettingsData }) {
  const isAdmin = data.can_edit_billing ?? data.is_admin;
  const update = useUpdateCustomerSettings();
  const s = data.settings;

  // Local editable copy, re-synced when the source data changes.
  const [billingEmail, setBillingEmail] = useState(s.billing_email ?? '');
  const [accountingEmail, setAccountingEmail] = useState(s.accounting_email ?? '');
  const [remittanceEmail, setRemittanceEmail] = useState(s.remittance_email ?? '');
  const [optionalEmail, setOptionalEmail] = useState(s.optional_email ?? '');
  const [statementsEnabled, setStatementsEnabled] = useState(s.statements_enabled);
  const [remindersEnabled, setRemindersEnabled] = useState(s.reminders_enabled);
  const [paperStatementsEnabled, setPaperStatementsEnabled] = useState(s.paper_statements_enabled);
  const [paperInvoicesEnabled, setPaperInvoicesEnabled] = useState(s.paper_invoices_enabled);
  const [invoiceRecipients, setInvoiceRecipients] = useState<BillingRole[]>(
    (s.invoice_recipients as BillingRole[]) ?? [],
  );
  const [statementRecipients, setStatementRecipients] = useState<BillingRole[]>(
    (s.statement_recipients as BillingRole[]) ?? [],
  );
  const [reminderRecipients, setReminderRecipients] = useState<BillingRole[]>(
    (s.reminder_recipients as BillingRole[]) ?? [],
  );
  const [orderUpdateRecipients, setOrderUpdateRecipients] = useState<BillingRole[]>(
    (s.order_update_recipients as BillingRole[]) ?? ['billing'],
  );
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  useEffect(() => {
    setBillingEmail(s.billing_email ?? '');
    setAccountingEmail(s.accounting_email ?? '');
    setRemittanceEmail(s.remittance_email ?? '');
    setOptionalEmail(s.optional_email ?? '');
    setStatementsEnabled(s.statements_enabled);
    setRemindersEnabled(s.reminders_enabled);
    setPaperStatementsEnabled(s.paper_statements_enabled);
    setPaperInvoicesEnabled(s.paper_invoices_enabled);
    setInvoiceRecipients((s.invoice_recipients as BillingRole[]) ?? []);
    setStatementRecipients((s.statement_recipients as BillingRole[]) ?? []);
    setReminderRecipients((s.reminder_recipients as BillingRole[]) ?? []);
    setOrderUpdateRecipients((s.order_update_recipients as BillingRole[]) ?? ['billing']);
  }, [s]);

  const emailByKey: Record<string, string> = {
    billing_email: billingEmail,
    accounting_email: accountingEmail,
    remittance_email: remittanceEmail,
    optional_email: optionalEmail,
  };
  const setEmailByKey: Record<string, (v: string) => void> = {
    billing_email: setBillingEmail,
    accounting_email: setAccountingEmail,
    remittance_email: setRemittanceEmail,
    optional_email: setOptionalEmail,
  };
  const recipientsByKey: Record<string, BillingRole[]> = {
    invoice_recipients: invoiceRecipients,
    statement_recipients: statementRecipients,
    reminder_recipients: reminderRecipients,
    order_update_recipients: orderUpdateRecipients,
  };
  const setRecipientsByKey: Record<string, (v: BillingRole[]) => void> = {
    invoice_recipients: setInvoiceRecipients,
    statement_recipients: setStatementRecipients,
    reminder_recipients: setReminderRecipients,
    order_update_recipients: setOrderUpdateRecipients,
  };

  function toggleRole(key: RoutingKey, role: BillingRole) {
    const cur = recipientsByKey[key] ?? [];
    const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
    setRecipientsByKey[key]!(next);
  }

  async function save() {
    setFeedback(null);
    const payload: UpdateSettingsInput = {
      billing_email: billingEmail.trim() || null,
      accounting_email: accountingEmail.trim() || null,
      remittance_email: remittanceEmail.trim() || null,
      optional_email: optionalEmail.trim() || null,
      statements_enabled: statementsEnabled,
      reminders_enabled: remindersEnabled,
      paper_statements_enabled: paperStatementsEnabled,
      paper_invoices_enabled: paperInvoicesEnabled,
      invoice_recipients: invoiceRecipients,
      statement_recipients: statementRecipients,
      reminder_recipients: reminderRecipients,
      order_update_recipients: orderUpdateRecipients,
    };
    try {
      await update.mutateAsync(payload);
      setFeedback({ kind: 'ok', msg: 'Billing notifications saved.' });
    } catch (err) {
      setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <Section
      icon={<Bell size={18} />}
      title="Notifications &amp; mailing"
      subtitle="Choose who gets invoices, statements, and overdue reminders by email."
    >
      {/* Toggles */}
      <div data-tour="notif-email" className="space-y-3">
        <Toggle
          icon={<ReceiptText size={15} />}
          label="Email me monthly statements"
          description="A summary of open invoices and aging, sent monthly."
          checked={statementsEnabled}
          disabled={!isAdmin}
          onChange={setStatementsEnabled}
        />
        <Toggle
          icon={<Bell size={15} />}
          label="Email me overdue reminders"
          description="Friendly nudges as invoices approach and pass their due date."
          checked={remindersEnabled}
          disabled={!isAdmin}
          onChange={setRemindersEnabled}
        />
      </div>

      {/* Paper mail (postal) — distinct from the email toggles above */}
      <div data-tour="notif-paper" className="mt-5">
        <div className="mb-2 text-xs font-semibold tracking-tight text-slate-900">
          Paper mail (postal)
        </div>
        <p className="mb-2 text-[11px] tracking-tight text-slate-900/70">
          These send physical copies through the mail (USPS), separate from the email
          notifications above.
        </p>
        <div className="space-y-3">
          <Toggle
            icon={<Printer size={15} />}
            label="Mail me paper statements"
            description="A printed monthly account statement mailed to your billing address."
            checked={paperStatementsEnabled}
            disabled={!isAdmin}
            onChange={setPaperStatementsEnabled}
          />
          <Toggle
            icon={<Printer size={15} />}
            label="Mail me paper invoice copies"
            description="Printed copies of new invoices mailed to your billing address."
            checked={paperInvoicesEnabled}
            disabled={!isAdmin}
            onChange={setPaperInvoicesEnabled}
          />
        </div>
      </div>

      {/* Email addresses */}
      <div data-tour="notif-addresses" className="mt-5">
        <div className="mb-2 text-xs font-semibold tracking-tight text-slate-900">
          Billing contacts
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {EMAIL_FIELDS.map((f) => (
            <FieldLabel key={f.key} label={f.label}>
              <Input
                type="email"
                value={emailByKey[f.key]}
                onChange={(e) => setEmailByKey[f.key]!(e.target.value)}
                placeholder="name@company.com"
                disabled={!isAdmin}
              />
            </FieldLabel>
          ))}
        </div>
      </div>

      {/* Routing */}
      <div data-tour="notif-routing" className="mt-5">
        <div className="mb-2 text-xs font-semibold tracking-tight text-slate-900">
          Who receives what
        </div>
        <div className="divide-y divide-brix-border overflow-hidden rounded-xl border border-brix-border bg-brix-surface">
          {EMAIL_FIELDS.map((ef) => (
            <div
              key={ef.key}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <span className="text-sm font-medium tracking-tight text-slate-900">{ef.label}</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {ROUTING_FIELDS.map((rf) => {
                  const active = (recipientsByKey[rf.key] ?? []).includes(ef.role);
                  return (
                    <button
                      key={rf.key}
                      type="button"
                      disabled={!isAdmin}
                      onClick={() => toggleRole(rf.key, ef.role)}
                      className={cn(
                        'rounded-md border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
                        active
                          ? 'border-brix-primary bg-brix-primary text-white'
                          : 'border-brix-border bg-brix-surface text-slate-900 hover:bg-brix-surface-alt',
                        !isAdmin && 'cursor-not-allowed opacity-60',
                        isAdmin && 'cursor-pointer',
                      )}
                    >
                      {rf.chip}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] italic tracking-tight text-slate-900/70">
          Each selected type is emailed to that address. If a type has no address selected, it
          falls back to your Primary Email.
        </p>
      </div>

      {isAdmin && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" onClick={save} disabled={update.isPending}>
            {update.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
            {update.isPending ? 'Saving…' : 'Save notifications'}
          </Button>
          {feedback && (
            <span
              className={cn(
                'text-xs tracking-tight',
                feedback.kind === 'ok' ? 'text-emerald-700' : 'text-red-600',
              )}
            >
              {feedback.msg}
            </span>
          )}
        </div>
      )}
    </Section>
  );
}

/* ============================== primitives ============================== */

export function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-brix-primary-deep bg-brix-surface shadow-card-deep">
      <div className="flex items-start gap-3 bg-brix-primary-deep px-6 py-4 md:px-8">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-bold leading-none tracking-tight text-white">
            {title}
          </h2>
          <p className="mt-1.5 text-sm leading-snug tracking-tight text-white/85">{subtitle}</p>
        </div>
      </div>
      <div className="px-6 pb-6 pt-6 md:px-8">{children}</div>
    </div>
  );
}

function Toggle({
  icon,
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-xl border border-brix-border bg-brix-surface px-4 py-3',
        disabled ? 'cursor-default' : 'cursor-pointer',
      )}
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brix-primary-tint text-brix-primary-deep">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold tracking-tight text-slate-900">{label}</span>
        <span className="mt-0.5 block text-xs tracking-tight text-slate-900/70">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1.5 h-4 w-4 cursor-pointer accent-brix-primary disabled:cursor-not-allowed"
      />
    </label>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs font-semibold tracking-tight text-slate-900">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  danger,
  loading,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border-2 border-brix-primary-deep bg-white shadow-card-deep">
        <div
          className={cn(
            'flex items-center justify-between px-6 py-4',
            danger ? 'bg-red-600' : 'bg-brix-primary-deep',
          )}
        >
          <h3 className="font-display text-lg font-bold leading-none tracking-tight text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-white hover:bg-white/15"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 pb-4 pt-5">
          <div className="text-sm leading-relaxed tracking-tight text-slate-900">{body}</div>
          <div className="mt-5 flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onConfirm}
              disabled={loading}
              className={cn(danger && 'bg-red-600 text-white hover:bg-red-700')}
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : null}
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
